import { useEffect, useState } from "react";
import { adminApi, type IntegrationHealthEntry, type ProviderServiceRow } from "../lib/admin-api";

const CAPABILITY_LABELS: Record<string, string> = {
  rates: "Rates",
  addressValidation: "Address validation",
  shipmentCreation: "Shipment creation",
  directPayment: "Direct sender payment",
  providerHostedPayment: "Hosted checkout",
  labels: "Labels",
  printerless: "Printerless",
  dropoffSearch: "Drop-off search",
  collection: "Collection",
  tracking: "Tracking",
  cancellation: "Cancellation",
};

/** Providers backed by an admin-maintained static rate table (no confirmed live quote endpoint) — see docs/low-volume-carrier-strategy.md. */
const RATE_TABLE_PROVIDERS: Record<string, { extraFieldKey: string; extraFieldLabel: string; extraFieldDefault: string; noteLine: string }> = {
  parcel_monkey: {
    extraFieldKey: "parcelMonkeyServiceCode",
    extraFieldLabel: "Parcel Monkey service code (e.g. evri_standard)",
    extraFieldDefault: "",
    noteLine: "Parcel Monkey's GetQuote response schema isn't confirmed yet — enter your actual expected price per service.",
  },
  // Parcel2Go is deliberately absent here — it has a real, confirmed live
  // quote endpoint (POST /quotes), so it never needs an admin-maintained
  // rate table. See shared/providers/parcel2go-provider.ts.
  royal_mail: {
    extraFieldKey: "royalMailServiceCode",
    extraFieldLabel: "Royal Mail service code (from your OBA service-codes list, e.g. CRL48)",
    extraFieldDefault: "CRL48",
    noteLine: "Royal Mail's API has no live quote endpoint at all (confirmed) — enter your actual negotiated OBA tariff.",
  },
};

export default function IntegrationHealth() {
  const [providers, setProviders] = useState<IntegrationHealthEntry[]>([]);
  const [loading, setLoading] = useState(true);

  async function refresh() {
    setLoading(true);
    const res = await adminApi.getIntegrationHealth();
    setProviders(res.providers);
    setLoading(false);
  }

  useEffect(() => {
    refresh();
  }, []);

  return (
    <div className="stack">
      <h2>Integrations</h2>
      <p className="field-help">
        Carrier strategy: AGGREGATOR_PRIMARY — Parcel Monkey and Parcel2Go, per docs/low-volume-carrier-strategy.md. Royal
        Mail and Sendcloud are preserved as secondary/fallback options. Capabilities reflect the researched carrier surface;
        a green health check only confirms credentials are valid, not that every capability listed is fully wired yet —
        see each adapter's source comments.
      </p>
      <div className="banner banner-warn">
        <span>
          Parcel2Go's sender-hosted checkout (<code>prepareCheckout</code>) is implemented against the real API but is{" "}
          <strong>experimental and not used by the live payment flow</strong> — Stripe remains the default. See
          docs/parcel2go-payment-validation.md for what's confirmed vs. what still needs a live sandbox test.
        </span>
      </div>
      <button className="btn btn-secondary" style={{ width: "fit-content" }} onClick={refresh} disabled={loading}>
        {loading ? "Checking…" : "Re-check now"}
      </button>

      {providers.map((p) => (
        <div className="card stack-sm" key={p.id}>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <strong>{p.displayName}</strong>
            <span className={`banner ${p.health.ok ? "banner-success" : "banner-danger"}`} style={{ padding: "2px 10px", borderRadius: 999 }}>
              {p.health.ok ? "Healthy" : "Not connected"}
            </span>
          </div>
          <div className="field-help">DB status: {p.dbStatus} · {p.kind}</div>
          {!p.health.ok && p.health.detail && <div className="field-help">{p.health.detail}</div>}
          <div className="tag-row">
            {Object.entries(p.capabilities).map(([key, val]) => (
              <span key={key} className={`tag ${val ? "" : "tag-outline"}`} style={!val ? { color: "var(--color-text-muted)", borderColor: "var(--color-border)" } : undefined}>
                {CAPABILITY_LABELS[key] ?? key}
              </span>
            ))}
          </div>
          {p.notes && <div className="field-help">{p.notes}</div>}
          {RATE_TABLE_PROVIDERS[p.id] && <RateTableEditor providerId={p.id} config={RATE_TABLE_PROVIDERS[p.id]!} />}
        </div>
      ))}
    </div>
  );
}

function RateTableEditor({ providerId, config }: { providerId: string; config: (typeof RATE_TABLE_PROVIDERS)[string] }) {
  const [bands, setBands] = useState<ProviderServiceRow[]>([]);
  const [showForm, setShowForm] = useState(false);

  async function refresh() {
    const res = await adminApi.listProviderServices(providerId);
    setBands(res.services);
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerId]);

  return (
    <div className="card stack-sm" style={{ background: "var(--color-surface-alt)" }}>
      <strong>Rate table (admin-maintained)</strong>
      <p className="field-help" style={{ margin: 0 }}>
        {config.noteLine} Nothing here is invented — the wizard will show exactly these prices and no others.
      </p>
      {bands.length === 0 && <div className="field-help">No rate bands configured yet — this provider will report NOT_CONFIGURED and show no quotes.</div>}
      {bands.map((b) => {
        const band = JSON.parse(b.metadata_json) as Record<string, unknown>;
        const extra = config.extraFieldKey ? (band[config.extraFieldKey] as string | undefined) : undefined;
        return (
          <div key={b.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>
              {String(band.serviceDisplayName)}
              {extra ? ` (${extra})` : ""} — up to {(Number(band.maxWeightGrams) / 1000).toFixed(1)}kg — £{Number(band.priceGbp).toFixed(2)}
            </span>
            <button
              className="btn btn-secondary"
              style={{ width: "auto" }}
              onClick={async () => {
                await adminApi.toggleRateBand(b.id, b.active !== 1);
                await refresh();
              }}
            >
              {b.active === 1 ? "Disable" : "Enable"}
            </button>
          </div>
        );
      })}
      <button className="btn btn-secondary" style={{ width: "fit-content" }} onClick={() => setShowForm((v) => !v)}>
        {showForm ? "Cancel" : "Add rate band"}
      </button>
      {showForm && (
        <RateBandForm
          providerId={providerId}
          config={config}
          onSaved={async () => {
            setShowForm(false);
            await refresh();
          }}
        />
      )}
    </div>
  );
}

function RateBandForm({
  providerId,
  config,
  onSaved,
}: {
  providerId: string;
  config: (typeof RATE_TABLE_PROVIDERS)[string];
  onSaved: () => void;
}) {
  const [service, setService] = useState("standard");
  const [serviceDisplayName, setServiceDisplayName] = useState("Standard");
  const [extraFieldValue, setExtraFieldValue] = useState(config.extraFieldDefault);
  const [maxWeightKg, setMaxWeightKg] = useState("2");
  const [priceGbp, setPriceGbp] = useState("");
  const [estimatedDeliveryDays, setEstimatedDeliveryDays] = useState("2-3 working days");
  const [trackedLevel, setTrackedLevel] = useState<"NONE" | "BASIC" | "FULL">("BASIC");
  const [compensationCoverGbp, setCompensationCoverGbp] = useState("50");
  const [saving, setSaving] = useState(false);

  return (
    <div className="stack-sm">
      <div className="field">
        <label>Internal service id</label>
        <input value={service} onChange={(e) => setService(e.target.value)} />
      </div>
      <div className="field">
        <label>Display name</label>
        <input value={serviceDisplayName} onChange={(e) => setServiceDisplayName(e.target.value)} />
      </div>
      {config.extraFieldKey && (
        <div className="field">
          <label>{config.extraFieldLabel}</label>
          <input value={extraFieldValue} onChange={(e) => setExtraFieldValue(e.target.value)} />
        </div>
      )}
      <div className="field">
        <label>Max weight (kg) for this band</label>
        <input value={maxWeightKg} onChange={(e) => setMaxWeightKg(e.target.value)} />
      </div>
      <div className="field">
        <label>Price (£) — your actual expected cost per shipment</label>
        <input value={priceGbp} onChange={(e) => setPriceGbp(e.target.value)} />
      </div>
      <div className="field">
        <label>Estimated delivery</label>
        <input value={estimatedDeliveryDays} onChange={(e) => setEstimatedDeliveryDays(e.target.value)} />
      </div>
      <div className="field">
        <label>Tracked level</label>
        <select value={trackedLevel} onChange={(e) => setTrackedLevel(e.target.value as typeof trackedLevel)}>
          <option value="NONE">None</option>
          <option value="BASIC">Basic</option>
          <option value="FULL">Full</option>
        </select>
      </div>
      <div className="field">
        <label>Compensation cover (£)</label>
        <input value={compensationCoverGbp} onChange={(e) => setCompensationCoverGbp(e.target.value)} />
      </div>
      <button
        className="btn btn-primary"
        style={{ width: "fit-content" }}
        disabled={saving || !priceGbp}
        onClick={async () => {
          setSaving(true);
          await adminApi.createRateBand({
            providerId,
            carrierCode: providerId,
            rateBand: {
              service,
              serviceDisplayName,
              ...(config.extraFieldKey ? { [config.extraFieldKey]: extraFieldValue } : {}),
              maxWeightGrams: Math.round(Number(maxWeightKg) * 1000),
              priceGbp: Number(priceGbp),
              estimatedDeliveryDays,
              trackedLevel,
              compensationCoverGbp: Number(compensationCoverGbp),
            },
          });
          setSaving(false);
          onSaved();
        }}
      >
        Save rate band
      </button>
    </div>
  );
}
